import { css } from '@emotion/react';
import { TooltipWrapper } from '../../display/TooltipWrapper';
import { Spinner } from '../../display/Spinner';
import { kebabCaseAriaAttrs } from '../../../utils/formatAriaAttrKeys';
import { Animation, Colors, Typography } from '../../../theme';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
const Styles = labelStyles('ToggleSwitch', {
    root: (props) => [
        css `
      ${Typography.paragraph3Bold};
      align-items: stretch;
      color: ${Colors.neutral800};
      cursor: pointer;
      display: inline-flex;
      overflow: hidden;
      border: ${`1px solid ${Colors.neutral600}`};
      transition: all ${Animation.transitionDuration} ease-in-out;

      &:focus-within:not(:hover) {
        box-shadow: 0 0 0 2px ${Colors.blue};
        border-color: transparent;
      }
    `,
        props.size === 'small' &&
            withModifier(props.size, css `
          width: 40px;
          height: 22px;
          border-radius: 20px;
          font-size: 0px;
          line-height: 0px;
          padding: 3px;
        `),
        props.size === 'medium' &&
            withModifier(props.size, css `
          min-width: 60px;
          height: 44px;
          border-radius: 30px;
          padding: 6px;
        `),
        !props.checked &&
            withModifier('unchecked', css `
          background-color: ${Colors.neutral200};
        `),
        props.checked &&
            withModifier('checked', css `
          background-color: ${Colors.success100};
          border: ${`1px solid ${Colors.success400}`};
        `),
        props.indeterminate &&
            withModifier('indeterminate', css `
          background-color: ${Colors.warning200};
        `),
        (props.disabled || props.isLoading) &&
            withModifier('disabled', css `
          background-color: ${Colors.neutral200};
          color: ${Colors.neutral500};
          cursor: not-allowed;
          border: ${`1px solid ${Colors.neutral600}`};
        `),
    ],
    innerWrap: css `
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  `,
    input: css `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    z-index: -1;
  `,
    spinner: [
        css `
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
    `,
    ],
    text: (props) => [
        css `
      display: grid;
      // Define to allow 'textInner' nodes to easily stack
      // on top of each other while preserving their widths,
      // setting up the layout for a nice transition.
      grid-template-areas: 'text';
      width: 100%;
      user-select: none;
      text-align: center;
      transition: padding ${Animation.transitionDuration} ease-in-out;
    `,
        !props.checked &&
            withModifier(`unchecked-${props.size}`, {
                paddingLeft: 'calc(30px + 8px)',
                paddingRight: 0,
            }),
        props.checked &&
            withModifier(`checked-${props.size}`, {
                paddingLeft: 0,
                paddingRight: 'calc(30px + 8px)',
            }),
        props.indeterminate &&
            withModifier('indeterminate', css `
          padding-left: 0;
          padding-right: 0;
        `),
        props.isLoading &&
            withModifier('loading', css `
          visibility: hidden;
          height: 0;
        `),
    ],
    textInner: ({ isHidden }) => [
        css `
      // See note at 'grid-template-areas' definition.
      grid-area: text;
      transition: opacity 0.1s ease-in-out;
    `,
        isHidden &&
            withModifier('hidden', css `
          opacity: 0;
        `),
    ],
    dot: (props) => [
        css `
      display: block;
      border-radius: 100%;
      background-color: ${Colors.neutral600};
      position: absolute;
      top: 0;
      transition: all ${Animation.transitionDuration} ease-in-out;
      width: 30px;
      height: 30px;
    `,
        !props.checked &&
            withModifier('unchecked', css `
          left: 0;
          transform: translateX(0);
        `),
        props.checked &&
            withModifier('checked', css `
          background-color: ${Colors.success400};
          left: 100%;
          transform: translateX(-100%);
        `),
        props.disabled &&
            withModifier('disabled', css `
          background-color: ${Colors.neutral400};
        `),
        props.isLoading &&
            withModifier('loading', css `
          visibility: hidden;
          height: 0;
        `),
    ],
});
const InnerText = ({ children, isHidden, }) => {
    return (isolateComponent("span", { "aria-hidden": "true", css: Styles.textInner({ isHidden }) }, children));
};
const getAriaLabel = (props) => {
    if (props.ariaLabel) {
        return props.ariaLabel;
    }
    if (props.isLoading) {
        return 'Loading';
    }
    if (props.indeterminate) {
        return typeof props.indeterminateText === 'string'
            ? props.indeterminateText
            : 'Indeterminate';
    }
    return props.checked ? props.checkedText : props.uncheckedText;
};
const getTooltipContent = (props) => props.disabled && props.showDisabledTooltip ? 'Disabled' : '';
export const ToggleSwitch = (props) => {
    return (isolateComponent(TooltipWrapper, { content: getTooltipContent(props) }, ({ childProps }) => (isolateComponent("label", Object.assign({}, kebabCaseAriaAttrs(childProps), { css: Styles.root(props) }),
        isolateComponent("span", { css: Styles.innerWrap },
            isolateComponent("input", { "aria-disabled": props.disabled || props.isLoading, "aria-label": getAriaLabel(props), checked: props.checked, css: Styles.input, disabled: props.disabled || props.isLoading, id: props.id, name: props.name, onChange: props.onChange, type: "checkbox", value: props.value }),
            props.isLoading && (isolateComponent("span", { css: Styles.spinner },
                isolateComponent(Spinner, null))),
            isolateComponent("span", { css: Styles.text(props) },
                isolateComponent(InnerText, { isHidden: props.indeterminate || !!props.checked }, props.uncheckedText),
                isolateComponent(InnerText, { isHidden: props.indeterminate || !props.checked }, props.checkedText),
                isolateComponent(InnerText, { isHidden: !props.indeterminate }, props.indeterminateText)),
            !props.indeterminate && !props.isLoading && (isolateComponent("span", { "aria-hidden": "true", css: Styles.dot(props), role: "presentation" })))))));
};
ToggleSwitch.defaultProps = {
    checkedText: 'On',
    indeterminateText: 'N/A',
    isLoading: false,
    onChange: () => { },
    showDisabledTooltip: true,
    size: 'medium',
    uncheckedText: 'Off',
};
//# sourceMappingURL=ToggleSwitch.js.map
// @zapier/design-system@6.1.0 (fd86aa2ab)
